import React, { useState, useEffect } from 'react';
import { Bookmark, Heart, Plus, Check, Loader } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Watchlist Button
export const WatchlistButton = ({ contentId, variant = 'button' }) => {
  const { token, isAuthenticated } = useAuth();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      checkStatus();
    }
  }, [isAuthenticated, token, contentId]);

  const checkStatus = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/user/content-status/${contentId}?authorization=Bearer ${token}`
      );
      const data = await response.json();
      setInWatchlist(data.in_watchlist);
    } catch (error) {
      console.error('Failed to check watchlist status:', error);
    }
  };

  const toggleWatchlist = async () => {
    if (!isAuthenticated) {
      alert('Please sign in to add to watchlist');
      return;
    }

    setLoading(true);
    try {
      const method = inWatchlist ? 'DELETE' : 'POST';
      await fetch(
        `${BACKEND_URL}/api/user/watchlist/${contentId}?authorization=Bearer ${token}`,
        { method }
      );
      setInWatchlist(!inWatchlist);
    } catch (error) {
      console.error('Failed to update watchlist:', error);
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={toggleWatchlist}
        disabled={loading}
        className={`p-2 rounded-full transition-all ${
          inWatchlist
            ? 'bg-red-600 text-white'
            : 'bg-gray-800/80 text-white hover:bg-gray-700'
        }`}
        title={inWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist'}
        data-testid="watchlist-icon-btn"
      >
        {loading ? (
          <Loader size={20} className="animate-spin" />
        ) : inWatchlist ? (
          <Check size={20} />
        ) : (
          <Plus size={20} />
        )}
      </button>
    );
  }

  return (
    <button
      onClick={toggleWatchlist}
      disabled={loading}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
        inWatchlist
          ? 'bg-red-600 text-white'
          : 'bg-gray-800 hover:bg-gray-700 text-white'
      }`}
      data-testid="watchlist-btn"
    >
      {loading ? (
        <Loader size={18} className="animate-spin" />
      ) : inWatchlist ? (
        <Check size={18} />
      ) : (
        <Bookmark size={18} />
      )}
      <span>{inWatchlist ? 'In Watchlist' : 'Add to Watchlist'}</span>
    </button>
  );
};

// Favorite Button
export const FavoriteButton = ({ contentId, variant = 'button' }) => {
  const { token, isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      checkStatus();
    }
  }, [isAuthenticated, token, contentId]);

  const checkStatus = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/user/content-status/${contentId}?authorization=Bearer ${token}`
      );
      const data = await response.json();
      setIsFavorite(data.in_favorites);
    } catch (error) {
      console.error('Failed to check favorite status:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      alert('Please sign in to add favorites');
      return;
    }

    setLoading(true);
    try {
      const method = isFavorite ? 'DELETE' : 'POST';
      await fetch(
        `${BACKEND_URL}/api/user/favorites/${contentId}?authorization=Bearer ${token}`,
        { method }
      );
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('Failed to update favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={toggleFavorite}
        disabled={loading}
        className={`p-2 rounded-full transition-all ${
          isFavorite
            ? 'bg-pink-600 text-white'
            : 'bg-gray-800/80 text-white hover:bg-gray-700'
        }`}
        title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        data-testid="favorite-icon-btn"
      >
        {loading ? (
          <Loader size={20} className="animate-spin" />
        ) : (
          <Heart size={20} fill={isFavorite ? 'currentColor' : 'none'} />
        )}
      </button>
    );
  }

  return (
    <button
      onClick={toggleFavorite}
      disabled={loading}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
        isFavorite
          ? 'bg-pink-600 text-white'
          : 'bg-gray-800 hover:bg-gray-700 text-white'
      }`}
      data-testid="favorite-btn"
    >
      {loading ? (
        <Loader size={18} className="animate-spin" />
      ) : (
        <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
      )}
      <span>{isFavorite ? 'Favorited' : 'Add to Favorites'}</span>
    </button>
  );
};

// Watchlist Page Component
export const WatchlistPage = () => {
  const { token, isAuthenticated } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('watchlist');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [watchlistRes, favoritesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/user/watchlist?authorization=Bearer ${token}`),
        fetch(`${BACKEND_URL}/api/user/favorites?authorization=Bearer ${token}`)
      ]);
      
      const watchlistData = await watchlistRes.json();
      const favoritesData = await favoritesRes.json();
      
      setWatchlist(watchlistData.items || []);
      setFavorites(favoritesData.items || []);
    } catch (error) {
      console.error('Failed to fetch user content:', error);
    } finally {
      setLoading(false);
    }
  };

  const items = activeTab === 'watchlist' ? watchlist : favorites;

  return (
    <div className="min-h-screen bg-black pt-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6">My List</h1>
        
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('watchlist')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'watchlist'
                ? 'bg-red-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <Bookmark size={20} />
            Watchlist ({watchlist.length})
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'favorites'
                ? 'bg-pink-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <Heart size={20} />
            Favorites ({favorites.length})
          </button>
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">
              {activeTab === 'watchlist' ? '📺' : '❤️'}
            </div>
            <h2 className="text-xl text-white mb-2">
              {activeTab === 'watchlist' ? 'Your watchlist is empty' : 'No favorites yet'}
            </h2>
            <p className="text-gray-400">
              {activeTab === 'watchlist'
                ? 'Add movies and shows to watch later'
                : 'Mark your favorite content to find it easily'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="group relative aspect-[2/3] rounded-lg overflow-hidden cursor-pointer"
              >
                <img
                  src={item.poster || item.thumbnail || '/placeholder.jpg'}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold">{item.title}</h3>
                    <p className="text-gray-400 text-sm capitalize">{item.content_type}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistButton;
